import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  Button,
  Stack,
  Paper,
  Avatar,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack,
  LinkedIn,
  Email,
  Business,
  Engineering,
  TrendingUp,
  Group,
  Lightbulb,
  CheckCircle,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

export const AboutTeam: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const leadership = [
    {
      name: 'John Mwangi',
      title: 'Chief Executive Officer',
      bio: 'Visionary leader with over 15 years of experience in manufacturing and industrial development. Passionate about innovation and sustainable growth.',
      image: '/api/uploads/team/john-mwangi.jpg',
      linkedin: 'https://linkedin.com/in/john-mwangi',
      email: 'john.mwangi@monioproduction.com',
      expertise: ['Strategic Planning', 'Business Development', 'Innovation'],
    },
    {
      name: 'Sarah Kamau',
      title: 'Chief Operations Officer',
      bio: 'Operations expert with deep knowledge of lean manufacturing and process optimization. Committed to operational excellence and quality management.',
      image: '/api/uploads/team/sarah-kamau.jpg',
      linkedin: 'https://linkedin.com/in/sarah-kamau',
      email: 'sarah.kamau@monioproduction.com',
      expertise: ['Operations Management', 'Quality Control', 'Process Optimization'],
    },
    {
      name: 'David Ochieng',
      title: 'Chief Technology Officer',
      bio: 'Technology innovator driving digital transformation and automation initiatives. Expert in IoT, AI, and smart manufacturing solutions.',
      image: '/api/uploads/team/david-ochieng.jpg',
      linkedin: 'https://linkedin.com/in/david-ochieng',
      email: 'david.ochieng@monioproduction.com',
      expertise: ['Digital Transformation', 'Automation', 'IoT & AI'],
    },
    {
      name: 'Grace Wanjiku',
      title: 'Chief Financial Officer',
      bio: 'Financial strategist with expertise in corporate finance, risk management, and sustainable business practices.',
      image: '/api/uploads/team/grace-wanjiku.jpg',
      linkedin: 'https://linkedin.com/in/grace-wanjiku',
      email: 'grace.wanjiku@monioproduction.com',
      expertise: ['Financial Strategy', 'Risk Management', 'Sustainability'],
    },
  ];

  const departmentHeads = [
    {
      name: 'Michael Odhiambo',
      title: 'Head of Engineering',
      bio: 'Leading our engineering team with expertise in mechanical and electrical systems design.',
      image: '/api/uploads/team/michael-odhiambo.jpg',
      department: 'Engineering',
    },
    {
      name: 'Jane Akinyi',
      title: 'Head of Quality Assurance',
      bio: 'Ensuring the highest standards of quality across all our manufacturing processes.',
      image: '/api/uploads/team/jane-akinyi.jpg',
      department: 'Quality Assurance',
    },
    {
      name: 'Peter Kiprop',
      title: 'Head of Supply Chain',
      bio: 'Managing our supply chain operations and strategic partnerships.',
      image: '/api/uploads/team/peter-kiprop.jpg',
      department: 'Supply Chain',
    },
    {
      name: 'Mary Njeri',
      title: 'Head of Human Resources',
      bio: 'Building and nurturing our talented team and company culture.',
      image: '/api/uploads/team/mary-njeri.jpg',
      department: 'Human Resources',
    },
  ];

  const teamStats = [
    { label: 'Team Members', value: '150+', icon: <Group /> },
    { label: 'Years Experience', value: '15+', icon: <Business /> },
    { label: 'Projects Completed', value: '500+', icon: <CheckCircle /> },
    { label: 'Innovation Awards', value: '25+', icon: <Lightbulb /> },
  ];

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/about')}
            sx={{ mb: 3 }}
          >
            Back to About
          </Button>
          
          <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
            Meet Our Team
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
            The passionate professionals driving innovation and excellence at Monio Production
          </Typography>
          
          <Typography variant="body1" sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}>
            Our diverse team of experts brings together decades of experience in manufacturing, technology, 
            and business development. We're united by our commitment to innovation, quality, and sustainable growth.
          </Typography>
        </Paper>

        {/* Team Stats */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Grid container spacing={3}>
            {teamStats.map((stat, index) => (
              <Grid item xs={6} md={3} key={index}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{
                      width: 60,
                      height: 60,
                      mx: 'auto',
                      mb: 2,
                      background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                    {stat.value}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {stat.label}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Leadership Team */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
            Leadership Team
          </Typography>
          
          <Grid container spacing={4}>
            {leadership.map((leader, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  sx={{
                    p: 3,
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar
                      src={leader.image}
                      sx={{
                        width: 80,
                        height: 80,
                        mr: 3,
                        border: '3px solid #6366f1',
                      }}
                    />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        {leader.name}
                      </Typography>
                      <Typography variant="body1" color="primary" sx={{ fontWeight: 500 }}>
                        {leader.title}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>
                    {leader.bio}
                  </Typography>
                  
                  <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                    {leader.expertise.map((skill, skillIndex) => (
                      <Chip
                        key={skillIndex}
                        label={skill}
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                        }}
                      />
                    ))}
                  </Stack>
                  
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      href={leader.linkedin}
                      target="_blank"
                      sx={{
                        color: '#0077b5',
                        '&:hover': { background: 'rgba(0, 119, 181, 0.1)' },
                      }}
                    >
                      <LinkedIn />
                    </IconButton>
                    <IconButton
                      size="small"
                      href={`mailto:${leader.email}`}
                      sx={{
                        color: '#6366f1',
                        '&:hover': { background: 'rgba(99, 102, 241, 0.1)' },
                      }}
                    >
                      <Email />
                    </IconButton>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Department Heads */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
            Department Leaders
          </Typography>
          
          <Grid container spacing={3}>
            {departmentHeads.map((head, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Card
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0px 20px 40px rgba(0, 0, 0, 0.1)',
                    },
                  }}
                >
                  <Avatar
                    src={head.image}
                    sx={{
                      width: 80,
                      height: 80,
                      mx: 'auto',
                      mb: 2,
                      border: '3px solid #6366f1',
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    {head.name}
                  </Typography>
                  <Typography variant="body2" color="primary" sx={{ fontWeight: 500, mb: 1 }}>
                    {head.title}
                  </Typography>
                  <Chip
                    label={head.department}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {head.bio}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Company Culture */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, textAlign: 'center', mb: 4 }}>
            Our Culture
          </Typography>
          
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                What Drives Us
              </Typography>
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Innovation and continuous improvement in everything we do" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Collaboration and teamwork across all departments" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Commitment to sustainability and environmental responsibility" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircle sx={{ color: 'primary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Excellence in quality and customer satisfaction" />
                </ListItem>
              </List>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
                Why Join Us
              </Typography>
              <List>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <TrendingUp sx={{ color: 'success.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Opportunities for professional growth and development" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Lightbulb sx={{ color: 'warning.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Work on cutting-edge technologies and innovative projects" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Group sx={{ color: 'info.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Collaborative and inclusive work environment" />
                </ListItem>
                <ListItem sx={{ px: 0 }}>
                  <ListItemIcon>
                    <Engineering sx={{ color: 'secondary.main' }} />
                  </ListItemIcon>
                  <ListItemText primary="Make a real impact in East Africa's manufacturing sector" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            border: '1px solid rgba(255, 255, 255, 0.2)',
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Join Our Team
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            We're always looking for talented individuals who share our passion for innovation and excellence. 
            Explore career opportunities and become part of our growing team.
          </Typography>
          
          <Stack direction="row" spacing={3} justifyContent="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/careers')}
              sx={{
                px: 4,
                py: 1.5,
                background: 'linear-gradient(135deg, #1b4d89 0%, #0f3460 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0f3460 0%, #1b4d89 100%)',
                },
              }}
            >
              View Careers
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/contact')}
              sx={{
                px: 4,
                py: 1.5,
                border: '2px solid #6366f1',
                color: '#6366f1',
                '&:hover': {
                  border: '2px solid #4f46e5',
                  background: 'rgba(99, 102, 241, 0.04)',
                },
              }}
            >
              Contact Us
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}; 